import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useAlert } from '../context/AlertContext';
import { formatDate } from '../utils/dateUtils';

const StatutoryReportForm = ({ hazard, forms, onClose }) => {
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  // `forms` = resolved statutory form entries for the incident's jurisdiction
  // (one form_family). modal_type drives which country-specific fields render.
  const reportType = forms?.[0]?.modal_type || null;
  const [formData, setFormData] = useState({
    // India specific
    factory_name: '',
    factory_address: '',
    factory_license_no: '',
    manager_name: '',
    // Seeded from the incident record so the reporter does not retype the time
    // on every statutory export. Stays editable — the backend renderers use the
    // same fallback chain (form_data → hazard.incident_time → derived).
    incident_time: hazard?.incident_time || '',

    // India MP Form 21 specific (india_form21 modal type) — prefilled from
    // site metadata (Admin → Org Hierarchy → edit site), editable per export
    esic_employer_code: '',
    factory_registration_no: '',
    esic_local_office: '',
    occupier_name: '',
    occupier_address: '',
    occupier_phone: '',
    nature_of_industry: '',
    incident_shift: '',
    department_section: '',
    exact_place: '',
    how_it_took_place: '',
    involved_explosion: false,
    involved_fire: false,
    involved_toxic_emission: false,
    substances_emitted: '',
    injured_inside_factory: '',
    injured_outside_factory: '',
    killed_inside_factory: '',
    killed_outside_factory: '',
    witness_1: '',
    witness_2: '',
    cause_of_accident: '',
    signatory_name: '',
    signatory_designation: 'Manager',
    signatory_address: '',
    signatory_phone: '',
    // India MH Form 24 specific (india_form24 modal type)
    signatory_place: '',

    // Singapore specific
    company_address: '',
    company_uen: '',
    employment_type: 'Permanent',

    // Malaysia specific — shared JKKP 6 & 7
    roc_no: '',
    jkkp_registration_no: '',
    industrial_classification_code: '',
    notifier_name: '',
    notifier_designation: '',
    notifier_contact: '',
    contact_person_name: '',
    contact_person_number: '',
    date_first_reported_jkkp: '',
    // Malaysia JKKP 7 specific — medical practitioner
    doctor_name: '',
    doctor_designation: '',
    clinic_hospital_address: '',
    doctor_contact: '',
    // Malaysia JKKP 7 specific — diagnosis (health data — encrypted at rest)
    diagnosis: '',
    date_of_diagnosis: '',
    attending_doctor_name: '',
    attending_doctor_address: '',
    disease_description: '',
  });

  // Multiple injured persons support
  const emptyInjuredPerson = () => ({
    name: '', age: '', gender: '', department: '', nric: '', dob: '', designation: '', nature_of_injury: '',
    // Malaysia specific per-person fields
    ic_passport_no: '', nationality: '', occupation: '',
    // MP Form 21 annexure per-person fields (india_form21 modal type)
    adult_register_serial_no: '', address: '', precise_occupation: '', nature_of_job: '',
    cause_of_injury: '', injury_fatal: false, death_datetime: '', serious_injury_extent: '',
    disabled_over_48h: false, body_part_injured: '', activity_at_time: '',
    hazardous_process: false, dangerous_operation: false,
    work_start_hour: '', wages_payable_day: '',
    transport_passenger_to_work: false, transport_employer_permission: false,
    transport_operated_by_employer: false, transport_public_service: false,
    emergency_nature: '', emergency_employed_for_business: false,
    treating_physician_hospital: '', esic_dispensary_or_panel_doctor: '',
    // MH Form 24 per-person fields (india_form24 modal type)
    insurance_number: '', machine_name_part: '', machine_mechanical_power: false,
    contravention_of_law: false, contravention_of_orders: false,
    acting_without_instructions: false, contravention_for_business: false,
    returned_to_work: false, return_datetime: '',
  });
  const [injuredPersons, setInjuredPersons] = useState([emptyInjuredPerson()]);

  const [generating, setGenerating] = useState(false);
  const [loadingCompanyData, setLoadingCompanyData] = useState(true);

  // Auto-detect JKKP 6 vs JKKP 7 for Malaysia
  const isMalaysiaJkkp7 = reportType === 'malaysia' &&
    ['Occupational Disease', 'Occupational Poisoning'].includes(hazard?.hazard_type);
  const malaysiaFormType = isMalaysiaJkkp7 ? 'jkkp7' : 'jkkp6';

  // The specific form entry to generate: Malaysia picks JKKP 6 vs 7 by
  // incident type; every other family has a single entry.
  const activeForm = reportType === 'malaysia'
    ? (forms.find(f => f.renderer_key === (isMalaysiaJkkp7 ? 'my_jkkp7' : 'my_jkkp6')) || forms[0])
    : forms?.[0];

  // Fetch company settings and site data to pre-populate form
  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        // Fetch company settings
        const companyResponse = await axios.get(`${API}/company/settings`, { headers });
        const company = companyResponse.data || {};

        // Try to fetch site data from org hierarchy for address
        let siteAddress = '';
        let siteLicenseNo = '';
        let siteJkkpRegNo = '';
        // MP Form 21 statutory particulars from site metadata
        let siteStatutory = {};
        if (hazard?.site_id) {
          try {
            const siteResponse = await axios.get(`${API}/org/nodes/${hazard.site_id}`, { headers });
            const siteMeta = siteResponse.data?.node?.metadata || {};
            siteAddress = siteMeta.address || '';
            siteLicenseNo = siteMeta.factory_license_no || '';
            siteJkkpRegNo = siteMeta.jkkp_registration_no || '';
            siteStatutory = {
              esic_employer_code: siteMeta.esic_employer_code || '',
              factory_registration_no: siteMeta.factory_registration_no || '',
              esic_local_office: siteMeta.esic_local_office || '',
              occupier_name: siteMeta.occupier_name || '',
              occupier_address: siteMeta.occupier_address || '',
              occupier_phone: siteMeta.occupier_phone || '',
              nature_of_industry: siteMeta.nature_of_industry || '',
            };
          } catch {
            // Site data not available, fall back to company settings
          }
        }

        setFormData(prev => ({
          ...prev,
          // Pre-populate: factory name from company, address from site with company fallback
          factory_name: company.company_name || '',
          factory_address: siteAddress || company.address || '',
          factory_license_no: siteLicenseNo || company.factory_license_no || '',
          manager_name: company.manager_name || '',
          // Pre-populate Singapore fields: prefer site data, fall back to company settings
          company_address: siteAddress || company.address || '',
          company_uen: company.uen || prev.company_uen,
          // Pre-populate Malaysia fields
          notifier_name: company.manager_name || '',
          roc_no: company.roc_no || '',
          jkkp_registration_no: siteJkkpRegNo || company.jkkp_registration_no || '',
          // Pre-populate MP Form 21 fields from site metadata; occupier and
          // signatory fall back to the configured manager
          ...siteStatutory,
          occupier_name: siteStatutory.occupier_name || company.manager_name || '',
          signatory_name: company.manager_name || '',
        }));
      } catch (err) {
        console.log('Could not fetch company settings for pre-population');
      } finally {
        setLoadingCompanyData(false);
      }
    };

    fetchCompanySettings();
  }, [hazard?.site_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleInjuredPersonChange = (index, field, value) => {
    setInjuredPersons(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addInjuredPerson = () => {
    setInjuredPersons(prev => [...prev, emptyInjuredPerson()]);
  };

  const removeInjuredPerson = (index) => {
    if (injuredPersons.length <= 1) return;
    setInjuredPersons(prev => prev.filter((_, i) => i !== index));
  };

  const handleGenerate = async () => {
    // Validation
    const hasAtLeastOneName = injuredPersons.some(p => p.name.trim());

    if (reportType === 'india') {
      if (!hasAtLeastOneName || !formData.factory_name || !formData.manager_name) {
        showError('Please fill in all required fields (at least one injured person name, factory name, manager name)');
        return;
      }
    } else if (reportType === 'india_form21' || reportType === 'india_form24') {
      if (!hasAtLeastOneName || !formData.occupier_name.trim()) {
        showError('Please fill in all required fields (at least one injured person name, occupier name)');
        return;
      }
    } else if (reportType === 'singapore') {
      const hasNric = injuredPersons.some(p => p.nric && p.nric.trim());
      if (!hasAtLeastOneName || !hasNric || !formData.company_uen) {
        showError('Please fill in all required fields (at least one injured person with name & NRIC, company UEN)');
        return;
      }
    } else if (reportType === 'malaysia') {
      const hasIcPassport = injuredPersons.some(p => p.ic_passport_no && p.ic_passport_no.trim());
      if (!hasAtLeastOneName || !hasIcPassport || !formData.notifier_name) {
        showError('Please fill in required fields (at least one affected person with name & IC/Passport, notifier name)');
        return;
      }
      if (isMalaysiaJkkp7 && !formData.diagnosis) {
        showError('Please fill in the diagnosis field for JKKP 7 report');
        return;
      }
    }

    setGenerating(true);

    try {
      const token = localStorage.getItem('token');

      // Jurisdiction-resolved export: the backend re-verifies that form_key
      // applies to the incident's site (state/country org nodes) — fail closed.
      const endpoint = `/incidents/${hazard.id}/statutory-reports/export`;

      // Include injured persons array and backward-compatible single person fields
      const payload = {
        ...formData,
        // MP Form 21 item 8: fold the two witness inputs into the array shape
        // the renderer reads
        witnesses: [
          { name_address: formData.witness_1 || '' },
          { name_address: formData.witness_2 || '' },
        ],
        injured_persons: injuredPersons.filter(p => p.name.trim()),
        // Backward compatibility: first person's data as flat fields
        injured_person_name: injuredPersons[0]?.name || '',
        injured_person_age: injuredPersons[0]?.age || '',
        injured_person_gender: injuredPersons[0]?.gender || '',
        injured_person_department: injuredPersons[0]?.department || '',
        injured_person_nric: injuredPersons[0]?.nric || '',
        injured_person_dob: injuredPersons[0]?.dob || '',
        injured_person_designation: injuredPersons[0]?.designation || '',
      };

      const response = await axios.post(
        `${API}${endpoint}`,
        { form_key: activeForm.form_key, form_data: payload },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `${activeForm.filename_prefix || 'Statutory_Report'}_${hazard.id.substring(0, 8)}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      const reportLabel = activeForm.display_label || 'Statutory report';
      showSuccess(`${reportLabel} generated successfully!`);
      onClose();
    } catch (err) {
      showError('Failed to generate report. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const getFormTitle = () => activeForm?.display_label || 'Statutory Report';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full my-8 p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-[#1B5C94]">
                {getFormTitle()} - Data Entry
              </h2>
              {activeForm?.citation && (
                <p className="text-xs text-gray-500 mt-1">{activeForm.citation}</p>
              )}
              {activeForm?.is_fallback && (
                <p className="text-xs text-amber-600 mt-1">
                  Country-level default form — set the site's State in Admin → Org Hierarchy
                  (edit the site) if your state prescribes a different form number.
                </p>
              )}
              {activeForm && activeForm.verified === false && (
                <p className="text-xs text-amber-600 mt-1">
                  Form number pending compliance verification.
                </p>
              )}
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
            {loadingCompanyData && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                Loading company details...
              </div>
            )}

            {/* Malaysia JKKP 7 health data notice */}
            {reportType === 'malaysia' && isMalaysiaJkkp7 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                <strong>Health Data Notice:</strong> This form collects occupational disease/poisoning data.
                Diagnosis and medical practitioner information will be encrypted at rest per SOC 2 CC6.7.
              </div>
            )}

            {/* Incident Info (Read-only) */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="font-bold text-gray-800 mb-2">Incident Information</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="font-semibold">Title:</span> {hazard.title}</div>
                <div><span className="font-semibold">Date:</span> {formatDate(hazard.incident_date)}</div>
                <div><span className="font-semibold">Type:</span> {hazard.hazard_type}</div>
                <div><span className="font-semibold">Severity:</span> {hazard.severity}</div>
              </div>
              {reportType !== 'malaysia' && (
                <div className="mt-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Time of Accident
                  </label>
                  <input
                    type="time"
                    name="incident_time"
                    value={formData.incident_time}
                    onChange={handleChange}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                  />
                  <p className="text-xs text-gray-500 mt-1">Specify the time when the accident occurred</p>
                </div>
              )}
              {reportType === 'malaysia' && !isMalaysiaJkkp7 && (
                <div className="mt-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Masa Kejadian / Time of Incident
                  </label>
                  <input
                    type="time"
                    name="incident_time"
                    value={formData.incident_time}
                    onChange={handleChange}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                  />
                </div>
              )}
            </div>

            {/* Injured Person Details - Multiple */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-bold text-gray-800">
                  {reportType === 'malaysia' ? 'Orang yang Terlibat / Affected Person Details' : 'Injured Person Details'}
                </h3>
                <button
                  type="button"
                  onClick={addInjuredPerson}
                  className="text-sm bg-[#1B5C94] text-white px-3 py-1 rounded-lg hover:bg-[#164A7B] transition-colors"
                >
                  + Add Person
                </button>
              </div>
              {injuredPersons.map((person, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 mb-3 relative">
                  {injuredPersons.length > 1 && (
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-semibold text-gray-500">Person {index + 1}</span>
                      <button
                        type="button"
                        onClick={() => removeInjuredPerson(index)}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        {reportType === 'malaysia' ? 'Nama / Full Name' : 'Full Name'} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={person.name}
                        onChange={(e) => handleInjuredPersonChange(index, 'name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                        placeholder="Enter full name"
                      />
                    </div>
                    {reportType !== 'malaysia' && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Age</label>
                        <input
                          type="number"
                          value={person.age}
                          onChange={(e) => handleInjuredPersonChange(index, 'age', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                          placeholder="Age"
                        />
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        {reportType === 'malaysia' ? 'Jantina / Gender' : 'Gender'}
                      </label>
                      <select
                        value={person.gender}
                        onChange={(e) => handleInjuredPersonChange(index, 'gender', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      >
                        <option value="">Select</option>
                        <option value="Male">{reportType === 'malaysia' ? 'L (Lelaki)' : 'Male'}</option>
                        <option value="Female">{reportType === 'malaysia' ? 'P (Perempuan)' : 'Female'}</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    {reportType !== 'malaysia' && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Department</label>
                        <input
                          type="text"
                          value={person.department}
                          onChange={(e) => handleInjuredPersonChange(index, 'department', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                          placeholder="Department"
                        />
                      </div>
                    )}
                    {/* Nature of Injury - per person (India/Singapore) */}
                    {reportType !== 'malaysia' && (
                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Nature of Injury</label>
                        <textarea
                          value={person.nature_of_injury}
                          onChange={(e) => handleInjuredPersonChange(index, 'nature_of_injury', e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                          placeholder="Describe the nature of injury (e.g., burns, fractures, chemical exposure, etc.)"
                        />
                      </div>
                    )}
                    {/* Singapore-specific per-person fields */}
                    {reportType === 'singapore' && (
                      <>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">
                            NRIC/FIN <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={person.nric}
                            onChange={(e) => handleInjuredPersonChange(index, 'nric', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                            placeholder="Worker NRIC/FIN"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Date of Birth</label>
                          <input
                            type="date"
                            value={person.dob}
                            onChange={(e) => handleInjuredPersonChange(index, 'dob', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Designation</label>
                          <input
                            type="text"
                            value={person.designation}
                            onChange={(e) => handleInjuredPersonChange(index, 'designation', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                            placeholder="Job title"
                          />
                        </div>
                      </>
                    )}
                    {/* Malaysia-specific per-person fields */}
                    {reportType === 'malaysia' && (
                      <>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">
                            No. K/P atau No. Paspot / IC/Passport <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={person.ic_passport_no}
                            onChange={(e) => handleInjuredPersonChange(index, 'ic_passport_no', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                            placeholder="IC or Passport number"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Tarikh Lahir / Date of Birth</label>
                          <input
                            type="date"
                            value={person.dob}
                            onChange={(e) => handleInjuredPersonChange(index, 'dob', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Warganegara / Nationality</label>
                          <input
                            type="text"
                            value={person.nationality}
                            onChange={(e) => handleInjuredPersonChange(index, 'nationality', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                            placeholder="e.g., Malaysian, Indonesian"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Pekerjaan / Occupation</label>
                          <input
                            type="text"
                            value={person.occupation}
                            onChange={(e) => handleInjuredPersonChange(index, 'occupation', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                            placeholder="Job title / occupation"
                          />
                        </div>
                      </>
                    )}
                  </div>
                  {/* MP Form 21 Annexure — particulars of person injured/killed */}
                  {reportType === 'india_form21' && (
                    <details className="mt-3 border border-gray-200 rounded-lg">
                      <summary className="px-3 py-2 text-sm font-semibold text-[#1B5C94] cursor-pointer select-none">
                        Annexure details (Form 21) — particulars of person injured/killed
                      </summary>
                      <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Serial No. in register of adult workers</label>
                          <input
                            type="text"
                            value={person.adult_register_serial_no}
                            onChange={(e) => handleInjuredPersonChange(index, 'adult_register_serial_no', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Address</label>
                          <input
                            type="text"
                            value={person.address}
                            onChange={(e) => handleInjuredPersonChange(index, 'address', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Precise occupation</label>
                          <input
                            type="text"
                            value={person.precise_occupation}
                            onChange={(e) => handleInjuredPersonChange(index, 'precise_occupation', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Nature of job</label>
                          <input
                            type="text"
                            value={person.nature_of_job}
                            onChange={(e) => handleInjuredPersonChange(index, 'nature_of_job', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Cause of injury</label>
                          <select
                            value={person.cause_of_injury}
                            onChange={(e) => handleInjuredPersonChange(index, 'cause_of_injury', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                          >
                            <option value="">Select</option>
                            <option value="Explosion">Explosion</option>
                            <option value="Fire">Fire</option>
                            <option value="Emission of toxic substance">Emission of toxic substance</option>
                            <option value="Others">Others</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Location of injury (part of body)</label>
                          <input
                            type="text"
                            value={person.body_part_injured}
                            onChange={(e) => handleInjuredPersonChange(index, 'body_part_injured', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                            placeholder="e.g. right leg, left hand, left eye"
                          />
                        </div>
                        <div className="md:col-span-2 flex flex-wrap gap-4">
                          <label className="flex items-center gap-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={person.injury_fatal}
                              onChange={(e) => handleInjuredPersonChange(index, 'injury_fatal', e.target.checked)}
                              className="rounded border-gray-300"
                            />
                            Fatal
                          </label>
                          <label className="flex items-center gap-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={person.disabled_over_48h}
                              onChange={(e) => handleInjuredPersonChange(index, 'disabled_over_48h', e.target.checked)}
                              className="rounded border-gray-300"
                            />
                            Disabled for more than 48 hours
                          </label>
                          <label className="flex items-center gap-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={person.hazardous_process}
                              onChange={(e) => handleInjuredPersonChange(index, 'hazardous_process', e.target.checked)}
                              className="rounded border-gray-300"
                            />
                            Hazardous process
                          </label>
                          <label className="flex items-center gap-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={person.dangerous_operation}
                              onChange={(e) => handleInjuredPersonChange(index, 'dangerous_operation', e.target.checked)}
                              className="rounded border-gray-300"
                            />
                            Dangerous process/operation
                          </label>
                        </div>
                        {person.injury_fatal && (
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Time and date of death</label>
                            <input
                              type="datetime-local"
                              value={person.death_datetime}
                              onChange={(e) => handleInjuredPersonChange(index, 'death_datetime', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                            />
                          </div>
                        )}
                        <div className={person.injury_fatal ? '' : 'md:col-span-2'}>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">If serious, extent of injury</label>
                          <input
                            type="text"
                            value={person.serious_injury_extent}
                            onChange={(e) => handleInjuredPersonChange(index, 'serious_injury_extent', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                            placeholder="e.g. loss of limb, fracture, permanent impairment, severe burns"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-semibold text-gray-700 mb-1">What the person was doing at the time of accident</label>
                          <textarea
                            value={person.activity_at_time}
                            onChange={(e) => handleInjuredPersonChange(index, 'activity_at_time', e.target.value)}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Hour started work on the day</label>
                          <input
                            type="time"
                            value={person.work_start_hour}
                            onChange={(e) => handleInjuredPersonChange(index, 'work_start_hour', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Wages payable for the day (full/part)</label>
                          <input
                            type="text"
                            value={person.wages_payable_day}
                            onChange={(e) => handleInjuredPersonChange(index, 'wages_payable_day', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                            placeholder="e.g. Full wages payable"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <p className="text-sm font-semibold text-gray-700 mb-1">If the accident occurred while traveling in the employer's transport:</p>
                          <div className="flex flex-wrap gap-4">
                            <label className="flex items-center gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                checked={person.transport_passenger_to_work}
                                onChange={(e) => handleInjuredPersonChange(index, 'transport_passenger_to_work', e.target.checked)}
                                className="rounded border-gray-300"
                              />
                              Traveling as passenger to/from work
                            </label>
                            <label className="flex items-center gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                checked={person.transport_employer_permission}
                                onChange={(e) => handleInjuredPersonChange(index, 'transport_employer_permission', e.target.checked)}
                                className="rounded border-gray-300"
                              />
                              With employer's express/implied permission
                            </label>
                            <label className="flex items-center gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                checked={person.transport_operated_by_employer}
                                onChange={(e) => handleInjuredPersonChange(index, 'transport_operated_by_employer', e.target.checked)}
                                className="rounded border-gray-300"
                              />
                              Transport operated by/for the employer
                            </label>
                            <label className="flex items-center gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                checked={person.transport_public_service}
                                onChange={(e) => handleInjuredPersonChange(index, 'transport_public_service', e.target.checked)}
                                className="rounded border-gray-300"
                              />
                              Vehicle in ordinary public transport service
                            </label>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">If while meeting an emergency, its nature</label>
                          <input
                            type="text"
                            value={person.emergency_nature}
                            onChange={(e) => handleInjuredPersonChange(index, 'emergency_nature', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                          />
                        </div>
                        <div className="flex items-end pb-2">
                          <label className="flex items-center gap-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={person.emergency_employed_for_business}
                              onChange={(e) => handleInjuredPersonChange(index, 'emergency_employed_for_business', e.target.checked)}
                              className="rounded border-gray-300"
                            />
                            Employed for employer's trade/business at the premises
                          </label>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Physician/dispensary/hospital treating</label>
                          <input
                            type="text"
                            value={person.treating_physician_hospital}
                            onChange={(e) => handleInjuredPersonChange(index, 'treating_physician_hospital', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">ESIC dispensary/panel doctor</label>
                          <input
                            type="text"
                            value={person.esic_dispensary_or_panel_doctor}
                            onChange={(e) => handleInjuredPersonChange(index, 'esic_dispensary_or_panel_doctor', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                          />
                        </div>
                      </div>
                    </details>
                  )}
                  {/* MH Form 24 — per-person particulars (items 7-20) */}
                  {reportType === 'india_form24' && (
                    <details className="mt-3 border border-gray-200 rounded-lg">
                      <summary className="px-3 py-2 text-sm font-semibold text-[#1B5C94] cursor-pointer select-none">
                        Form 24 details (per person) — items 7–20
                      </summary>
                      <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Insurance Number (Item 7)</label>
                          <input
                            type="text"
                            value={person.insurance_number}
                            onChange={(e) => handleInjuredPersonChange(index, 'insurance_number', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Address of injured person (Item 8)</label>
                          <input
                            type="text"
                            value={person.address}
                            onChange={(e) => handleInjuredPersonChange(index, 'address', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Occupation (Item 9)</label>
                          <input
                            type="text"
                            value={person.precise_occupation}
                            onChange={(e) => handleInjuredPersonChange(index, 'precise_occupation', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Hour started work on the day (Item 11a)</label>
                          <input
                            type="time"
                            value={person.work_start_hour}
                            onChange={(e) => handleInjuredPersonChange(index, 'work_start_hour', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Wages payable for the day (Item 11b)</label>
                          <input
                            type="text"
                            value={person.wages_payable_day}
                            onChange={(e) => handleInjuredPersonChange(index, 'wages_payable_day', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                            placeholder="e.g. Full wages payable"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Machine and part causing accident (Item 12a)</label>
                          <input
                            type="text"
                            value={person.machine_name_part}
                            onChange={(e) => handleInjuredPersonChange(index, 'machine_name_part', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                            placeholder="Leave blank if not caused by machinery"
                          />
                        </div>
                        <div className="flex items-end pb-2">
                          <label className="flex items-center gap-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={person.machine_mechanical_power}
                              onChange={(e) => handleInjuredPersonChange(index, 'machine_mechanical_power', e.target.checked)}
                              className="rounded border-gray-300"
                            />
                            Moved by mechanical power at that time
                          </label>
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-semibold text-gray-700 mb-1">What the injured person was doing at that time (Item 12b)</label>
                          <textarea
                            value={person.activity_at_time}
                            onChange={(e) => handleInjuredPersonChange(index, 'activity_at_time', e.target.value)}
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <p className="text-sm font-semibold text-gray-700 mb-1">At the time of accident, was the injured person (Item 12c/d):</p>
                          <div className="flex flex-wrap gap-4">
                            <label className="flex items-center gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                checked={person.contravention_of_law}
                                onChange={(e) => handleInjuredPersonChange(index, 'contravention_of_law', e.target.checked)}
                                className="rounded border-gray-300"
                              />
                              Contravening any applicable law
                            </label>
                            <label className="flex items-center gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                checked={person.contravention_of_orders}
                                onChange={(e) => handleInjuredPersonChange(index, 'contravention_of_orders', e.target.checked)}
                                className="rounded border-gray-300"
                              />
                              Contravening occupier's orders
                            </label>
                            <label className="flex items-center gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                checked={person.acting_without_instructions}
                                onChange={(e) => handleInjuredPersonChange(index, 'acting_without_instructions', e.target.checked)}
                                className="rounded border-gray-300"
                              />
                              Acting without instructions
                            </label>
                            {(person.contravention_of_law || person.contravention_of_orders || person.acting_without_instructions) && (
                              <label className="flex items-center gap-2 text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  checked={person.contravention_for_business}
                                  onChange={(e) => handleInjuredPersonChange(index, 'contravention_for_business', e.target.checked)}
                                  className="rounded border-gray-300"
                                />
                                Act done for/in connection with occupier's trade or business
                              </label>
                            )}
                          </div>
                        </div>
                        <div className="md:col-span-2">
                          <p className="text-sm font-semibold text-gray-700 mb-1">If the accident occurred while traveling in the occupier's transport (Item 13):</p>
                          <div className="flex flex-wrap gap-4">
                            <label className="flex items-center gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                checked={person.transport_passenger_to_work}
                                onChange={(e) => handleInjuredPersonChange(index, 'transport_passenger_to_work', e.target.checked)}
                                className="rounded border-gray-300"
                              />
                              Traveling as passenger to/from work
                            </label>
                            <label className="flex items-center gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                checked={person.transport_employer_permission}
                                onChange={(e) => handleInjuredPersonChange(index, 'transport_employer_permission', e.target.checked)}
                                className="rounded border-gray-300"
                              />
                              With occupier's express/implied permission
                            </label>
                            <label className="flex items-center gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                checked={person.transport_operated_by_employer}
                                onChange={(e) => handleInjuredPersonChange(index, 'transport_operated_by_employer', e.target.checked)}
                                className="rounded border-gray-300"
                              />
                              Transport operated by/for the occupier
                            </label>
                            <label className="flex items-center gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                checked={person.transport_public_service}
                                onChange={(e) => handleInjuredPersonChange(index, 'transport_public_service', e.target.checked)}
                                className="rounded border-gray-300"
                              />
                              Vehicle in ordinary public transport service
                            </label>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">If while meeting an emergency, its nature (Item 14a)</label>
                          <input
                            type="text"
                            value={person.emergency_nature}
                            onChange={(e) => handleInjuredPersonChange(index, 'emergency_nature', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                          />
                        </div>
                        <div className="flex items-end pb-2">
                          <label className="flex items-center gap-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={person.emergency_employed_for_business}
                              onChange={(e) => handleInjuredPersonChange(index, 'emergency_employed_for_business', e.target.checked)}
                              className="rounded border-gray-300"
                            />
                            Employed for occupier's trade/business at the premises (Item 14b)
                          </label>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Location of injury (Item 17b)</label>
                          <input
                            type="text"
                            value={person.body_part_injured}
                            onChange={(e) => handleInjuredPersonChange(index, 'body_part_injured', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                            placeholder="e.g. right leg, left hand, left eye"
                          />
                        </div>
                        <div className="flex items-end pb-2 gap-4 flex-wrap">
                          <label className="flex items-center gap-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={person.injury_fatal}
                              onChange={(e) => handleInjuredPersonChange(index, 'injury_fatal', e.target.checked)}
                              className="rounded border-gray-300"
                            />
                            Fatal / person died (Item 20)
                          </label>
                          {!person.injury_fatal && (
                            <label className="flex items-center gap-2 text-sm text-gray-700">
                              <input
                                type="checkbox"
                                checked={person.returned_to_work}
                                onChange={(e) => handleInjuredPersonChange(index, 'returned_to_work', e.target.checked)}
                                className="rounded border-gray-300"
                              />
                              Returned to work (Item 18)
                            </label>
                          )}
                        </div>
                        {person.injury_fatal && (
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Date of death (Item 20ii)</label>
                            <input
                              type="datetime-local"
                              value={person.death_datetime}
                              onChange={(e) => handleInjuredPersonChange(index, 'death_datetime', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                            />
                          </div>
                        )}
                        {!person.injury_fatal && person.returned_to_work && (
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Date and hour of return to work (Item 18ii)</label>
                            <input
                              type="datetime-local"
                              value={person.return_datetime}
                              onChange={(e) => handleInjuredPersonChange(index, 'return_datetime', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                            />
                          </div>
                        )}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Physician/dispensary/hospital treating (Item 19a)</label>
                          <input
                            type="text"
                            value={person.treating_physician_hospital}
                            onChange={(e) => handleInjuredPersonChange(index, 'treating_physician_hospital', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Dispensary/panel doctor elected (Item 19b)</label>
                          <input
                            type="text"
                            value={person.esic_dispensary_or_panel_doctor}
                            onChange={(e) => handleInjuredPersonChange(index, 'esic_dispensary_or_panel_doctor', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                          />
                        </div>
                      </div>
                    </details>
                  )}
                </div>
              ))}
            </div>

            {/* India-specific fields */}
            {reportType === 'india' && (
              <div>
                <h3 className="font-bold text-gray-800 mb-3">Factory Details (India)</h3>
                {/* BUG-007: Statutory fields are read-only — auto-populated from company settings */}
                <p className="text-xs text-gray-500 mb-3">
                  These fields are auto-populated from Company Settings and cannot be edited here.
                  To update them, go to <strong>Admin → Company Settings</strong>.
                </p>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Factory Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="factory_name"
                      value={formData.factory_name}
                      readOnly
                      title="Auto-populated from Company Settings. Edit in Admin → Company Settings."
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 cursor-not-allowed text-gray-700"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Factory Address
                    </label>
                    <input
                      type="text"
                      name="factory_address"
                      value={formData.factory_address}
                      readOnly
                      title="Auto-populated from Company Settings. Edit in Admin → Company Settings."
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 cursor-not-allowed text-gray-700"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        License Number
                      </label>
                      <input
                        type="text"
                        name="factory_license_no"
                        value={formData.factory_license_no}
                        readOnly
                        title="Auto-populated from Company Settings. Edit in Admin → Company Settings."
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 cursor-not-allowed text-gray-700"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Manager Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="manager_name"
                        value={formData.manager_name}
                        readOnly
                        title="Auto-populated from Company Settings. Edit in Admin → Company Settings."
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 cursor-not-allowed text-gray-700"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* MP Form 21 fields (india_form21) — faithful Rule 103 layout.
                Prefilled from site metadata (Admin → Org Hierarchy → edit
                site) and editable per export. */}
            {reportType === 'india_form21' && (
              <div className="space-y-6">
                <div>
                  <h3 className="font-bold text-gray-800 mb-1">ESIC / Registration (form header)</h3>
                  <p className="text-xs text-gray-500 mb-3">
                    Prefilled from the site's statutory details (Admin → Org Hierarchy → edit site). You can adjust them for this export.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">ESIC Employer's Code Number</label>
                      <input
                        type="text"
                        name="esic_employer_code"
                        value={formData.esic_employer_code}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Registration Number</label>
                      <input
                        type="text"
                        name="factory_registration_no"
                        value={formData.factory_registration_no}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">License Number (as given in the licence)</label>
                      <input
                        type="text"
                        name="factory_license_no"
                        value={formData.factory_license_no}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Local ESIC Office (name and address)</label>
                      <input
                        type="text"
                        name="esic_local_office"
                        value={formData.esic_local_office}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-bold text-gray-800 mb-3">Factory &amp; Occupier (Items 1–3)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Factory Name</label>
                      <input
                        type="text"
                        name="factory_name"
                        value={formData.factory_name}
                        readOnly
                        title="Auto-populated from Company Settings."
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 cursor-not-allowed text-gray-700"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Factory Address</label>
                      <input
                        type="text"
                        name="factory_address"
                        value={formData.factory_address}
                        readOnly
                        title="Auto-populated from the site address / Company Settings."
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 cursor-not-allowed text-gray-700"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Occupier Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="occupier_name"
                        value={formData.occupier_name}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Occupier Telephone</label>
                      <input
                        type="text"
                        name="occupier_phone"
                        value={formData.occupier_phone}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Occupier Address</label>
                      <input
                        type="text"
                        name="occupier_address"
                        value={formData.occupier_address}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Nature of Industry (as given in the License)</label>
                      <input
                        type="text"
                        name="nature_of_industry"
                        value={formData.nature_of_industry}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-bold text-gray-800 mb-3">Accident Particulars (Items 4–6)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Shift</label>
                      <input
                        type="text"
                        name="incident_shift"
                        value={formData.incident_shift}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                        placeholder="e.g. General / A / B / Night"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Department / Section</label>
                      <input
                        type="text"
                        name="department_section"
                        value={formData.department_section}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Exact place where it took place</label>
                      <input
                        type="text"
                        name="exact_place"
                        value={formData.exact_place}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                        placeholder="Defaults to the incident location if left blank"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-1">6(a) How the accident / dangerous occurrence took place</label>
                      <textarea
                        name="how_it_took_place"
                        value={formData.how_it_took_place}
                        onChange={handleChange}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                      <p className="text-xs text-gray-500 mt-1">Leave blank to auto-generate from the incident narrative and investigation data.</p>
                    </div>
                    <div className="md:col-span-2 flex flex-wrap gap-4">
                      <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={formData.involved_explosion}
                          onChange={(e) => setFormData(prev => ({ ...prev, involved_explosion: e.target.checked }))}
                          className="rounded border-gray-300"
                        />
                        Involved Explosion
                      </label>
                      <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={formData.involved_fire}
                          onChange={(e) => setFormData(prev => ({ ...prev, involved_fire: e.target.checked }))}
                          className="rounded border-gray-300"
                        />
                        Involved Fire
                      </label>
                      <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={formData.involved_toxic_emission}
                          onChange={(e) => setFormData(prev => ({ ...prev, involved_toxic_emission: e.target.checked }))}
                          className="rounded border-gray-300"
                        />
                        Emission of toxic substance(s)
                      </label>
                    </div>
                    {formData.involved_toxic_emission && (
                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Substance(s) emitted</label>
                        <input
                          type="text"
                          name="substances_emitted"
                          value={formData.substances_emitted}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="font-bold text-gray-800 mb-1">Casualty Counts (Item 7)</h3>
                  <p className="text-xs text-gray-500 mb-3">Leave blank to auto-count from the injured person entries above (Fatal → killed).</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Injured — inside factory</label>
                      <input
                        type="number"
                        min="0"
                        name="injured_inside_factory"
                        value={formData.injured_inside_factory}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Injured — outside factory</label>
                      <input
                        type="number"
                        min="0"
                        name="injured_outside_factory"
                        value={formData.injured_outside_factory}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Killed — inside factory</label>
                      <input
                        type="number"
                        min="0"
                        name="killed_inside_factory"
                        value={formData.killed_inside_factory}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Killed — outside factory</label>
                      <input
                        type="number"
                        min="0"
                        name="killed_outside_factory"
                        value={formData.killed_outside_factory}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-bold text-gray-800 mb-3">Witnesses &amp; Cause (Items 8–9)</h3>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Witness 1 — name and address</label>
                      <input
                        type="text"
                        name="witness_1"
                        value={formData.witness_1}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Witness 2 — name and address</label>
                      <input
                        type="text"
                        name="witness_2"
                        value={formData.witness_2}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Cause of accident or dangerous occurrence</label>
                      <textarea
                        name="cause_of_accident"
                        value={formData.cause_of_accident}
                        onChange={handleChange}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                      <p className="text-xs text-gray-500 mt-1">Leave blank to auto-populate from the investigation root cause (5-Why / Fishbone).</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-bold text-gray-800 mb-3">Signature (Manager/Occupier)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Name</label>
                      <input
                        type="text"
                        name="signatory_name"
                        value={formData.signatory_name}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Signing as</label>
                      <select
                        name="signatory_designation"
                        value={formData.signatory_designation}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      >
                        <option value="Manager">Manager</option>
                        <option value="Occupier">Occupier</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Address</label>
                      <input
                        type="text"
                        name="signatory_address"
                        value={formData.signatory_address}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Telephone Number</label>
                      <input
                        type="text"
                        name="signatory_phone"
                        value={formData.signatory_phone}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* MH Form 24 fields (india_form24) — faithful Rule 115 layout.
                Prefilled from site metadata (Admin → Org Hierarchy → edit
                site) and editable per export. The full form repeats per
                injured person in the PDF; per-person items 7-20 live in the
                "Form 24 details" section of each person card above. */}
            {reportType === 'india_form24' && (
              <div className="space-y-6">
                <div>
                  <h3 className="font-bold text-gray-800 mb-1">Occupier &amp; Premises (Items 1–4)</h3>
                  <p className="text-xs text-gray-500 mb-3">
                    Prefilled from the site's statutory details (Admin → Org Hierarchy → edit site). You can adjust them for this export.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Occupier Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="occupier_name"
                        value={formData.occupier_name}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Occupier Address</label>
                      <input
                        type="text"
                        name="occupier_address"
                        value={formData.occupier_address}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Registration Number</label>
                      <input
                        type="text"
                        name="factory_registration_no"
                        value={formData.factory_registration_no}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Licence Number</label>
                      <input
                        type="text"
                        name="factory_license_no"
                        value={formData.factory_license_no}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Premises Address (Item 3)</label>
                      <input
                        type="text"
                        name="factory_address"
                        value={formData.factory_address}
                        readOnly
                        title="Auto-populated from the site address / Company Settings."
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 cursor-not-allowed text-gray-700"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Nature of Industry</label>
                      <input
                        type="text"
                        name="nature_of_industry"
                        value={formData.nature_of_industry}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Local ESIC Office to which attached (Item 10)</label>
                      <input
                        type="text"
                        name="esic_local_office"
                        value={formData.esic_local_office}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-bold text-gray-800 mb-3">Accident Particulars (Items 5, 15, 16)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Department / Section</label>
                      <input
                        type="text"
                        name="department_section"
                        value={formData.department_section}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Shift hours (if any)</label>
                      <input
                        type="text"
                        name="incident_shift"
                        value={formData.incident_shift}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                        placeholder="e.g. 08:00–16:00 (A shift)"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Exact place where the accident happened</label>
                      <input
                        type="text"
                        name="exact_place"
                        value={formData.exact_place}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                        placeholder="Defaults to the incident location if left blank"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-1">15. Describe briefly how the accident occurred</label>
                      <textarea
                        name="how_it_took_place"
                        value={formData.how_it_took_place}
                        onChange={handleChange}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                      <p className="text-xs text-gray-500 mt-1">Leave blank to auto-generate from the incident narrative and investigation data.</p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Witness 1 — name and address</label>
                      <input
                        type="text"
                        name="witness_1"
                        value={formData.witness_1}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Witness 2 — name and address</label>
                      <input
                        type="text"
                        name="witness_2"
                        value={formData.witness_2}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-bold text-gray-800 mb-3">Signature (Manager)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Place</label>
                      <input
                        type="text"
                        name="signatory_place"
                        value={formData.signatory_place}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Name</label>
                      <input
                        type="text"
                        name="signatory_name"
                        value={formData.signatory_name}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Designation</label>
                      <select
                        name="signatory_designation"
                        value={formData.signatory_designation}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      >
                        <option value="Manager">Manager</option>
                        <option value="Occupier">Occupier</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Singapore-specific fields */}
            {reportType === 'singapore' && (
              <div>
                <h3 className="font-bold text-gray-800 mb-3">Singapore-Specific Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Company UEN <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="company_uen"
                      value={formData.company_uen}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      placeholder="Unique Entity Number"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Company Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="company_address"
                      value={formData.company_address}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      placeholder="Registered company address"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Employment Type
                    </label>
                    <select
                      name="employment_type"
                      value={formData.employment_type}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                    >
                      <option value="Permanent">Permanent</option>
                      <option value="Contract">Contract</option>
                      <option value="Temporary">Temporary</option>
                    </select>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mb-2">
                  Singapore-specific person details (NRIC, DOB, Designation) are in each injured person card above.
                </p>
              </div>
            )}

            {/* Malaysia-specific fields */}
            {reportType === 'malaysia' && (
              <div>
                {/* Part A — Notifier / Employer Details */}
                <h3 className="font-bold text-gray-800 mb-3">Bahagian A: Maklumat Pemberitahu / Notifier Details (Employer)</h3>
                <p className="text-xs text-gray-500 mb-3">
                  Pre-populated from company settings where available.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Nama / Notifier Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="notifier_name"
                      value={formData.notifier_name}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      placeholder="Name of notifier"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Jawatan / Designation</label>
                    <input
                      type="text"
                      name="notifier_designation"
                      value={formData.notifier_designation}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      placeholder="e.g., Safety Manager"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Alamat Organisasi / Company Address</label>
                    <input
                      type="text"
                      name="company_address"
                      value={formData.company_address}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      placeholder="Organisation address"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">No. Telefon / Contact</label>
                    <input
                      type="text"
                      name="notifier_contact"
                      value={formData.notifier_contact}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      placeholder="Phone number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">No. R.O.C.</label>
                    <input
                      type="text"
                      name="roc_no"
                      value={formData.roc_no}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      placeholder="Company registration no."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">No. Pend. JKKP / JKKP Reg. No</label>
                    <input
                      type="text"
                      name="jkkp_registration_no"
                      value={formData.jkkp_registration_no}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      placeholder="JKKP registration number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Kod Klasifikasi Industri / Industry Code (Jadual 3)</label>
                    <input
                      type="text"
                      name="industrial_classification_code"
                      value={formData.industrial_classification_code}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      placeholder="Industry classification code"
                    />
                  </div>
                </div>

                {/* Contact person (if different) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Orang Dihubungi / Contact Person (if different)</label>
                    <input
                      type="text"
                      name="contact_person_name"
                      value={formData.contact_person_name}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      placeholder="Optional"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">No. Telefon / Contact Number</label>
                    <input
                      type="text"
                      name="contact_person_number"
                      value={formData.contact_person_number}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      placeholder="Optional"
                    />
                  </div>
                </div>

                {/* JKKP 6 specific: Date first reported */}
                {!isMalaysiaJkkp7 && (
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Tarikh Mula Lapor kepada JKKP / Date First Reported</label>
                    <input
                      type="date"
                      name="date_first_reported_jkkp"
                      value={formData.date_first_reported_jkkp}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                    />
                  </div>
                )}

                {/* JKKP 7 specific: Part A2 — Medical Practitioner */}
                {isMalaysiaJkkp7 && (
                  <>
                    <h3 className="font-bold text-gray-800 mb-3 mt-6">Bahagian A2: Pengamal Perubatan / Medical Practitioner</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Nama Doktor / Doctor Name</label>
                        <input
                          type="text"
                          name="doctor_name"
                          value={formData.doctor_name}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                          placeholder="Registered medical practitioner name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Jawatan / Designation</label>
                        <input
                          type="text"
                          name="doctor_designation"
                          value={formData.doctor_designation}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                          placeholder="e.g., Occupational Health Doctor"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Alamat Klinik/Hospital / Clinic Address</label>
                        <input
                          type="text"
                          name="clinic_hospital_address"
                          value={formData.clinic_hospital_address}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                          placeholder="Clinic or hospital address"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">No. Telefon / Doctor Contact</label>
                        <input
                          type="text"
                          name="doctor_contact"
                          value={formData.doctor_contact}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                          placeholder="Doctor's contact number"
                        />
                      </div>
                    </div>

                    {/* Part C — Diagnosis */}
                    <h3 className="font-bold text-gray-800 mb-3 mt-6">Bahagian C: Keracunan Pekerjaan / Diagnosis</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                          Diagnosis / Diagnosis Sementara <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="diagnosis"
                          value={formData.diagnosis}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                          placeholder="Diagnosis or provisional diagnosis"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Tarikh Diagnosis / Date of Diagnosis</label>
                        <input
                          type="date"
                          name="date_of_diagnosis"
                          value={formData.date_of_diagnosis}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Nama Doktor yang Merawat / Attending Doctor</label>
                        <input
                          type="text"
                          name="attending_doctor_name"
                          value={formData.attending_doctor_name}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                          placeholder="Attending doctor's name"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Alamat Doktor / Doctor Address</label>
                        <input
                          type="text"
                          name="attending_doctor_address"
                          value={formData.attending_doctor_address}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                          placeholder="Attending doctor's address"
                        />
                      </div>
                    </div>

                    {/* Part D — Disease Description */}
                    <h3 className="font-bold text-gray-800 mb-3 mt-6">Bahagian D: Huraian Pekerjaan / Work Description</h3>
                    <p className="text-xs text-gray-500 mb-2">
                      Describe any work done by the affected person that may have led to the disease. If caused by exposure to an agent (e.g., specific chemical), please state the agent.
                    </p>
                    <textarea
                      name="disease_description"
                      value={formData.disease_description}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5C94]"
                      placeholder="Huraikan pekerjaan yang membawa kepada keracunan/penyakit pekerjaan..."
                    />
                  </>
                )}

                <p className="text-xs text-gray-500 mt-3">
                  Malaysia-specific person details (IC/Passport, Nationality, Occupation) are in each affected person card above.
                </p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mt-6 pt-4 border-t">
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="flex-1 bg-[#1B5C94] text-white font-bold py-3 px-6 rounded-lg hover:bg-[#164A7B] transition-colors disabled:opacity-50"
            >
              {generating ? 'Generating PDF...' : 'Generate Report'}
            </button>
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gray-200 text-gray-700 font-bold rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatutoryReportForm;
